import { useEffect, useState } from 'react';
import { CreditCard, QrCode } from 'lucide-react';
import { fetchStaffDuitNowQrBlob, fetchStaffSiteLinks } from '../api/staff.js';

function PaymentIcon() {
  return (
    <div
      className="relative flex h-36 w-36 items-center justify-center rounded-full bg-rose-200 shadow-md"
      aria-hidden="true"
    >
      <CreditCard className="h-16 w-16 text-slate-800" strokeWidth={1.75} />
    </div>
  );
}

function DuitNowQrPlaceholder() {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let objectUrl;

    const loadQr = () => {
      setLoading(true);
      void fetchStaffSiteLinks()
        .then((links) => fetchStaffDuitNowQrBlob(links.qrRevision))
        .then((blob) => {
          if (cancelled) return;
          if (!blob) {
            setSrc((current) => {
              if (current) URL.revokeObjectURL(current);
              return null;
            });
            return;
          }
          objectUrl = URL.createObjectURL(blob);
          setSrc((current) => {
            if (current) URL.revokeObjectURL(current);
            return objectUrl;
          });
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    loadQr();
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadQr();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white p-4 shadow-lg">
      <p className="mb-4 text-center text-base leading-relaxed text-slate-700">
        Please make payment to this account
      </p>
      <div className="flex aspect-[25/16] w-full items-center justify-center overflow-hidden rounded-xl bg-white">
        {loading ? (
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
        ) : src ? (
          <img src={src} alt="DuitNow QR" className="h-full w-full object-contain" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-slate-400">
            <QrCode className="h-12 w-12" strokeWidth={1.5} />
            <span className="text-sm font-medium">No Image</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Payment() {
  const [paymentProofUrl, setPaymentProofUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    void fetchStaffSiteLinks().then((links) => {
      if (cancelled || !links.ok) return;
      setPaymentProofUrl(links.paymentProofUrl);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex min-h-[calc(100dvh-8.5rem)] flex-col items-center justify-start pt-3 pb-8 text-center">
      <DuitNowQrPlaceholder />

      <div className="mt-6 px-6">
        <PaymentIcon />
      </div>

      <p className="mt-8 max-w-xs px-6 text-base leading-relaxed text-slate-700">
        Please submit payment proof here:
      </p>

      {paymentProofUrl ? (
        <a
          href={paymentProofUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 text-lg font-bold text-slate-900 underline decoration-2 underline-offset-4 transition hover:text-brand-accent"
        >
          Tap here
        </a>
      ) : (
        <span className="mt-4 text-lg font-bold text-slate-400">Tap here</span>
      )}
    </div>
  );
}

export default Payment;
